import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Snackbar, TextField, Typography, useTheme,
} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import {REP_TYPES} from "../../constants";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import {useGroups} from "../../hooks/useGroups";
import {useGroup} from "../../hooks/useGroup";
import {useGroupCreate} from "../../hooks/useGroupCreate";
import {useGroupUpdate} from "../../hooks/useGroupUpdate";
import {useGroupDelete} from "../../hooks/useGroupDelete";
import {useClaimCategories} from "../../hooks/useClaimCategories";
import {useClaims} from "../../hooks/useClaims";
import {useSetGroupClaims} from "../../hooks/useSetGroupClaims";
import {getGroup as fetchGroup} from "../../services/GroupService";

// Stable per-category color palette (dot / tinted background / text). Categories are mapped to a
// slot by their display order, so a given category keeps its color across the page.
const CATEGORY_PALETTE = [
    {dot: "#4C8DFF", bg: "#EAF2FF", fg: "#1B5FD9"},
    {dot: "#12B886", bg: "#E4F7F0", fg: "#0B7A5A"},
    {dot: "#F59F00", bg: "#FFF5E1", fg: "#B27600"},
    {dot: "#E8590C", bg: "#FCEBE1", fg: "#C24408"},
    {dot: "#7048E8", bg: "#EEE9FB", fg: "#5433C7"},
    {dot: "#E64980", bg: "#FCE8F0", fg: "#C42B63"},
    {dot: "#495057", bg: "#EDEFF1", fg: "#343A40"},
];
const NEUTRAL_COLOR = {dot: "#8a8d96", bg: "rgba(0,0,0,0.04)", fg: "rgba(0,0,0,0.6)"};

const claimKey = (c) => `${c.claimType}::${c.claimValue}`;

// diff a baseline vs. staged set of claim keys into groupClaims entries with a `delete` flag
const diffEntries = (baseline, staged, makeEntry) => {
    const b = new Set(baseline), s = new Set(staged);
    const out = [];
    staged.forEach((v) => { if (!b.has(v)) out.push(makeEntry(v, false)); });
    baseline.forEach((v) => { if (!s.has(v)) out.push(makeEntry(v, true)); });
    return out;
};

const GroupsPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    const [selectedId, setSelectedId] = useState(null);
    const [modalType, setModalType] = useState(null);     // 'create' | 'details' | 'permissions' | null
    const [draft, setDraft] = useState(null);
    const [permFilter, setPermFilter] = useState([]);     // category ids active as view filters
    const [nameError, setNameError] = useState("");
    const [snackbar, setSnackbar] = useState({open: false, severity: "success", message: ""});

    useEffect(() => {
        const route = routes.find((r) => r.key === "groups");
        if (!route.visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [groups, refetchGroups, listLoading] = useGroups();
    const [detail, detailLoading, refetchDetail] = useGroup(selectedId, {enabled: selectedId != null});
    const [categories] = useClaimCategories();
    const [claims] = useClaims();

    const createMutation = useGroupCreate();
    const updateMutation = useGroupUpdate();
    const deleteMutation = useGroupDelete();
    const setGroupClaims = useSetGroupClaims();

    const accent = theme.palette.secondary.main;
    const showSnackbar = (severity, message) => setSnackbar({open: true, severity, message});
    const closeSnackbar = () => setSnackbar((prev) => ({...prev, open: false}));

    // ------- category lookups (color, label, order, totals) -------
    const sortedCategories = useMemo(
        () => [...categories].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
        [categories]
    );
    const categoryById = useMemo(() => {
        const m = new Map();
        categories.forEach((c) => m.set(c.id, c));
        return m;
    }, [categories]);
    const colorByCategoryId = useMemo(() => {
        const m = new Map();
        sortedCategories.forEach((c, i) => m.set(c.id, CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]));
        return m;
    }, [sortedCategories]);
    const colorFor = (categoryId) => colorByCategoryId.get(categoryId) ?? NEUTRAL_COLOR;
    const categoryTotal = (categoryId) => categoryById.get(categoryId)?.claimCount
        ?? claims.filter((c) => c.categoryId === categoryId).length;

    // claim catalog keyed by (claimType, claimValue), merged with the selected group's own claims
    // so members always resolve to a name/category even if the catalog is still loading.
    const claimByKey = useMemo(() => {
        const m = new Map();
        claims.forEach((c) => m.set(claimKey(c), c));
        (detail?.claims ?? []).forEach((c) => { if (!m.has(claimKey(c))) m.set(claimKey(c), c); });
        return m;
    }, [claims, detail]);

    const totalPermissions = claims.length;
    const selectedListItem = useMemo(() => groups.find((g) => g.id === selectedId) ?? null, [groups, selectedId]);

    const sortClaims = (list) => [...list].sort((a, b) => {
        const ca = categoryById.get(a.categoryId), cb = categoryById.get(b.categoryId);
        return (ca?.displayOrder ?? 0) - (cb?.displayOrder ?? 0)
            || (a.displayName ?? "").localeCompare(b.displayName ?? "");
    });

    const selectGroup = (id) => { setSelectedId(id); setPermFilter([]); };

    // ------------------------------ modals ------------------------------
    const openDetails = (kind) => {
        setNameError("");
        if (kind === "create") {
            setDraft({kind: "create", name: "", description: "", copyFromId: "", copyClaims: [], copyLoading: false});
        } else {
            const src = detail ?? selectedListItem;
            setDraft({kind: "edit", name: src?.name ?? "", description: src?.description ?? ""});
        }
        setModalType("details");
    };
    const openPermissions = () => {
        const baseline = (detail?.claims ?? []).map(claimKey);
        setDraft({baseline, keys: [...baseline], cat: "all", search: ""});
        setModalType("permissions");
    };
    const closeModal = () => { setModalType(null); setDraft(null); setNameError(""); };

    const chooseCopyFrom = (id) => {
        setDraft((d) => ({...d, copyFromId: id, copyClaims: [], copyLoading: !!id}));
        if (!id) return;
        fetchGroup(id)
            .then((g) => setDraft((d) => (d && d.copyFromId === id
                ? {...d, copyClaims: g.claims ?? [], copyLoading: false} : d)))
            .catch(() => setDraft((d) => (d && d.copyFromId === id ? {...d, copyLoading: false} : d)));
    };

    const saveDetails = () => {
        setNameError("");
        const name = (draft.name ?? "").trim();
        if (!name) { setNameError("Name is required."); return; }
        const description = (draft.description ?? "").trim();

        if (draft.kind === "create") {
            const claimSeed = draft.copyFromId
                ? (draft.copyClaims ?? []).map((c) => ({claimType: c.claimType, claimValue: c.claimValue}))
                : undefined;
            createMutation.mutate({name, description, claims: claimSeed}, {
                onSuccess: (created) => {
                    showSnackbar("success", claimSeed?.length
                        ? `Group created with ${claimSeed.length} permission${claimSeed.length !== 1 ? "s" : ""}.`
                        : "Group created.");
                    refetchGroups();
                    setSelectedId(created?.id ?? null);
                    setPermFilter([]);
                    closeModal();
                },
                onError: detailsSaveError,
            });
        } else {
            updateMutation.mutate({id: selectedId, payload: {name, description}}, {
                onSuccess: () => { showSnackbar("success", "Details updated."); refetchGroups(); refetchDetail(); closeModal(); },
                onError: detailsSaveError,
            });
        }
    };

    const detailsSaveError = (error) => {
        const status = error?.response?.status;
        if (status === 409) { setNameError("A group with this name already exists."); return; }
        if (status === 400) { showSnackbar("error", "One or more copied permissions no longer exist."); return; }
        if (status === 404) { showSnackbar("error", "This group no longer exists."); refetchGroups(); closeModal(); return; }
        showSnackbar("error", "Something went wrong. Please try again.");
    };

    const handleDelete = () => {
        const label = detail?.name ?? selectedListItem?.name ?? "this group";
        if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
        deleteMutation.mutate(selectedId, {
            onSuccess: () => {
                showSnackbar("success", "Group deleted.");
                refetchGroups();
                setSelectedId(null);
                closeModal();
            },
            onError: (error) => {
                const status = error?.response?.status;
                if (status === 409) {
                    showSnackbar("warning", "This group is still assigned to users. Reassign them before deleting it.");
                } else if (status === 404) {
                    showSnackbar("error", "This group no longer exists."); refetchGroups(); setSelectedId(null); closeModal();
                } else {
                    showSnackbar("error", "Something went wrong. Please try again.");
                }
            },
        });
    };

    const savePermissions = () => {
        const entries = diffEntries(draft.baseline, draft.keys, (key, del) => {
            const c = claimByKey.get(key);
            return {claimType: c?.claimType, claimValue: c?.claimValue, groupId: selectedId, delete: del};
        });
        if (!entries.length) { closeModal(); return; }
        setGroupClaims.mutate(entries, {
            onSuccess: () => { showSnackbar("success", "Permissions updated."); refetchDetail(); refetchGroups(); setPermFilter([]); closeModal(); },
            onError: (error) => {
                if (error?.response?.status === 400) showSnackbar("error", "One or more permissions were rejected. No changes applied.");
                else showSnackbar("error", "Something went wrong. Please try again.");
            },
        });
    };

    // ------------------------------ list ------------------------------
    const renderList = () => (
        <Box sx={{borderRight: `1px solid ${theme.palette.divider}`, display: "flex", flexDirection: "column", minHeight: 0, backgroundColor: "white"}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: `1px solid ${theme.palette.divider}`}}>
                <Typography variant="body1" sx={{fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary"}}>Groups ({groups.length})</Typography>
                <Button size="small" onClick={() => openDetails("create")} sx={{fontWeight: 700}}>+ New</Button>
            </Box>
            <Box sx={{flex: 1, overflowY: "auto", minHeight: 0}}>
                {listLoading && groups.length === 0 ? (
                    <Box sx={{display: "flex", justifyContent: "center", p: 4}}><CircularProgress size={24} /></Box>
                ) : groups.map((g) => {
                    const sel = g.id === selectedId;
                    return (
                        <Box key={g.id} onClick={() => selectGroup(g.id)}
                             sx={{display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, cursor: "pointer",
                                 borderBottom: `1px solid ${theme.palette.divider}`, borderLeft: `3px solid ${sel ? accent : "transparent"}`,
                                 backgroundColor: sel ? "rgba(0, 163, 255, 0.12)" : "transparent",
                                 "&:hover": {backgroundColor: sel ? "rgba(0, 163, 255, 0.12)" : theme.palette.background.secondary}}}>
                            <Box sx={{flex: "0 0 30px", height: 30, borderRadius: "4px", border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: sel ? accent : theme.palette.background.secondary, color: sel ? "white" : "text.secondary",
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700}}>
                                {g.claimCount}
                            </Box>
                            <Box sx={{minWidth: 0, flex: 1}}>
                                <Typography noWrap sx={{fontWeight: 500}}>{g.name}</Typography>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, color: "text.secondary", fontSize: "0.78rem"}}>
                                    <span>{g.claimCount} permission{g.claimCount !== 1 ? "s" : ""}</span>
                                    {g.userCount > 0 && <><span style={{opacity: 0.4}}>&middot;</span><span>{g.userCount} user{g.userCount !== 1 ? "s" : ""}</span></>}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );

    // ------------------------------ read-only detail ------------------------------
    const labelSx = {fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

    const sectionCard = (title, onEdit, body) => (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", mb: 2, overflow: "hidden"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.secondary}}>
                <Typography sx={{fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "text.primary"}}>{title}</Typography>
                {onEdit && <Button size="small" onClick={onEdit} sx={{ml: "auto", fontWeight: 700}}>Edit</Button>}
            </Box>
            <Box sx={{p: 2}}>{body}</Box>
        </Box>
    );

    // a colored permission pill (used in the read-only view and, with states, in the editor)
    const permPill = (key, label, categoryId, opts = {}) => {
        const col = colorFor(categoryId);
        const {state, onClick} = opts;
        return (
            <Box key={key} onClick={onClick}
                 sx={{display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.4, borderRadius: "999px",
                     border: `${state === "added" ? 2 : 1}px ${state === "removed" ? "dashed" : "solid"} ${col.dot}`,
                     backgroundColor: col.bg, color: col.fg, fontSize: "0.8rem",
                     fontWeight: state === "added" ? 700 : 500, opacity: state === "removed" ? 0.5 : 1,
                     cursor: onClick ? "pointer" : "default",
                     textDecoration: state === "removed" ? "line-through" : "none"}}>
                <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: col.dot, flex: "0 0 8px"}} />
                {state === "added" && <span>+</span>}
                <span>{label}</span>
                {onClick && <span style={{opacity: 0.6, fontSize: "0.95rem", lineHeight: 1}}>{state === "removed" ? "↺" : "×"}</span>}
            </Box>
        );
    };

    const legendButton = (cat, count, active, onClick, dim) => {
        const col = colorFor(cat.id);
        return (
            <Box key={cat.id} onClick={onClick}
                 sx={{display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, borderRadius: "999px", cursor: "pointer",
                     border: `1px solid ${active ? col.dot : theme.palette.divider}`, backgroundColor: active ? col.bg : "white",
                     fontSize: "0.78rem", fontWeight: 600, opacity: dim ? 0.4 : 1}}>
                <Box sx={{width: 9, height: 9, borderRadius: "50%", backgroundColor: col.dot}} />
                <span>{cat.displayName}</span>
                <span style={{color: theme.palette.text.secondary, fontWeight: 700}}>{count} of {categoryTotal(cat.id)}</span>
            </Box>
        );
    };

    const renderPermissionsView = () => {
        const groupClaims = detail?.claims ?? [];
        const presentCats = sortedCategories.filter((cat) => groupClaims.some((c) => c.categoryId === cat.id));
        const filtering = permFilter.length > 0;
        const toggleFilter = (id) => setPermFilter((f) => f.includes(id) ? f.filter((x) => x !== id) : [...f, id]);
        const shown = sortClaims(groupClaims).filter((c) => !filtering || permFilter.includes(c.categoryId));

        if (!groupClaims.length) {
            return <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem"}}>No permissions assigned yet. Use <b>Edit</b> to add some.</Typography>;
        }
        return (
            <>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2, pb: 2, borderBottom: `1px dashed ${theme.palette.divider}`}}>
                    {presentCats.map((cat) => {
                        const n = groupClaims.filter((c) => c.categoryId === cat.id).length;
                        return legendButton(cat, n, permFilter.includes(cat.id), () => toggleFilter(cat.id), filtering && !permFilter.includes(cat.id));
                    })}
                    <Typography sx={{fontSize: "0.72rem", color: "text.secondary", ml: 0.5}}>
                        {filtering ? <>Filtered — <Box component="span" onClick={() => setPermFilter([])} sx={{color: accent, cursor: "pointer"}}>clear</Box></> : "Click a category to filter"}
                    </Typography>
                </Box>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                    {shown.map((c) => permPill(claimKey(c), c.displayName || c.claimValue, c.categoryId))}
                </Box>
            </>
        );
    };

    const renderDetail = () => {
        if (selectedId == null) {
            return <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", p: 5}}><Typography sx={{fontStyle: "italic"}}>Select a group to view its details</Typography></Box>;
        }
        if (detailLoading && !detail) {
            return <Box sx={{display: "flex", justifyContent: "center", p: 5}}><CircularProgress size={28} /></Box>;
        }
        const group = detail ?? selectedListItem;
        if (!group) return null;
        const granted = detail ? detail.claims.length : (group.claimCount ?? 0);
        const total = totalPermissions || granted;
        const users = detail?.userCount ?? group.userCount ?? 0;
        return (
            <Box sx={{p: 4}}>
                <Box sx={{mb: 3}}>
                    <Typography variant="h6" sx={{fontWeight: 500}}>{group.name}</Typography>
                    <Typography sx={{color: "text.secondary", fontSize: "0.85rem", mt: 0.5}}>
                        {users} user{users !== 1 ? "s" : ""} assigned &middot; {granted} of {total} permissions
                    </Typography>
                </Box>

                {sectionCard("Details", () => openDetails("edit"), (
                    <Box sx={{display: "grid", gap: 2}}>
                        <Box><Typography sx={labelSx}>Name</Typography><Typography>{group.name}</Typography></Box>
                        <Box><Typography sx={labelSx}>Description</Typography>
                            <Typography>{group.description || <span style={{color: theme.palette.text.disabled, fontStyle: "italic"}}>No description</span>}</Typography>
                        </Box>
                    </Box>
                ))}

                {sectionCard(`Permissions (${granted})`, openPermissions, renderPermissionsView())}
            </Box>
        );
    };

    // ------------------------------ modals ------------------------------
    const renderDetailsModal = () => {
        const isCreate = draft.kind === "create";
        return (
            <>
                <DialogContent dividers>
                    <TextField label="Name" size="small" fullWidth required value={draft.name}
                               error={!!nameError} helperText={nameError || " "}
                               onChange={(e) => { setNameError(""); setDraft((d) => ({...d, name: e.target.value})); }} />
                    <TextField label="Description" size="small" fullWidth sx={{mt: 1}} value={draft.description}
                               onChange={(e) => setDraft((d) => ({...d, description: e.target.value}))} />
                    {isCreate && (
                        <TextField label="Copy Permissions From" size="small" select fullWidth sx={{mt: 2}}
                                   value={draft.copyFromId} onChange={(e) => chooseCopyFrom(e.target.value)}
                                   helperText={draft.copyLoading ? "Loading permissions…"
                                       : (draft.copyFromId ? `Will copy ${draft.copyClaims.length} permission${draft.copyClaims.length !== 1 ? "s" : ""}.` : "Optional — start from scratch or clone another group's permissions.")}>
                            <MenuItem value=""><em>— Start from scratch —</em></MenuItem>
                            {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name} ({g.claimCount} permissions)</MenuItem>)}
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions sx={{justifyContent: "space-between"}}>
                    <Box>{!isCreate && <Button color="error" onClick={handleDelete} disabled={deleteMutation.isPending}>Delete</Button>}</Box>
                    <Box>
                        <Button onClick={closeModal}>Cancel</Button>
                        <Button variant="contained" onClick={saveDetails} disabled={createMutation.isPending || updateMutation.isPending}>{isCreate ? "Create" : "Save"}</Button>
                    </Box>
                </DialogActions>
            </>
        );
    };

    const renderPermissionsModal = () => {
        const staged = new Set(draft.keys);
        const baseline = new Set(draft.baseline);
        const selInCat = (id) => draft.keys.filter((k) => claimByKey.get(k)?.categoryId === id).length;
        const availInCat = (id) => claims.filter((c) => c.categoryId === id && !staged.has(claimKey(c)) && !baseline.has(claimKey(c))).length;
        const availAll = claims.filter((c) => !staged.has(claimKey(c)) && !baseline.has(claimKey(c))).length;

        const toggleKey = (key) => setDraft((d) => ({...d, keys: d.keys.includes(key) ? d.keys.filter((k) => k !== key) : [...d.keys, key]}));
        const setCat = (v) => setDraft((d) => ({...d, cat: v}));

        // union of baseline + staged, sorted, filtered by category
        const unionKeys = Array.from(new Set([...draft.baseline, ...draft.keys]));
        let unionClaims = sortClaims(unionKeys.map((k) => claimByKey.get(k)).filter(Boolean));
        if (draft.cat !== "all") unionClaims = unionClaims.filter((c) => c.categoryId === Number(draft.cat));

        const q = (draft.search ?? "").trim().toLowerCase();
        let available = claims.filter((c) => !staged.has(claimKey(c)) && !baseline.has(claimKey(c)));
        if (draft.cat !== "all") available = available.filter((c) => c.categoryId === Number(draft.cat));
        if (q) available = available.filter((c) => (c.displayName ?? "").toLowerCase().includes(q) || (c.claimValue ?? "").toLowerCase().includes(q));
        available = sortClaims(available);

        const filtering = draft.cat !== "all";

        return (
            <>
                <DialogContent dividers>
                    <Typography sx={labelSx}>Permission Categories</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mb: 2, pb: 2, borderBottom: `1px dashed ${theme.palette.divider}`}}>
                        {sortedCategories.map((cat) => {
                            const active = draft.cat === String(cat.id);
                            return legendButton(cat, selInCat(cat.id), active,
                                () => setCat(active ? "all" : String(cat.id)), filtering && !active);
                        })}
                    </Box>

                    <Typography sx={labelSx}>Permissions ({draft.keys.length})</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 30, mb: 2}}>
                        {unionClaims.length ? unionClaims.map((c) => {
                            const key = claimKey(c);
                            const inD = staged.has(key), inB = baseline.has(key);
                            const state = inD && !inB ? "added" : (!inD && inB ? "removed" : undefined);
                            return permPill(key, c.displayName || c.claimValue, c.categoryId, {state, onClick: () => toggleKey(key)});
                        }) : <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.82rem"}}>{draft.cat === "all" ? "No permissions selected" : "No permissions in this category"}</Typography>}
                    </Box>

                    <Box sx={{display: "flex", gap: 1, mb: 1}}>
                        <TextField size="small" select value={draft.cat} onChange={(e) => setCat(e.target.value)} sx={{flex: "0 0 200px"}}>
                            <MenuItem value="all">All categories ({availAll} available)</MenuItem>
                            {sortedCategories.map((cat) => <MenuItem key={cat.id} value={String(cat.id)}>{cat.displayName} ({availInCat(cat.id)} available)</MenuItem>)}
                        </TextField>
                        <TextField size="small" fullWidth placeholder="Search permissions…" value={draft.search}
                                   onChange={(e) => setDraft((d) => ({...d, search: e.target.value}))} />
                    </Box>
                    <Box sx={{maxHeight: "34vh", overflowY: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px"}}>
                        {available.length ? available.map((c) => {
                            const col = colorFor(c.categoryId);
                            return (
                                <Box key={claimKey(c)} onClick={() => toggleKey(claimKey(c))}
                                     sx={{display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, cursor: "pointer",
                                         borderBottom: `1px solid ${theme.palette.divider}`, "&:hover": {backgroundColor: theme.palette.background.secondary}}}>
                                    <Box sx={{width: 9, height: 9, borderRadius: "50%", backgroundColor: col.dot, flex: "0 0 9px"}} />
                                    <Typography sx={{flex: 1, fontSize: "0.875rem"}}>{c.displayName || c.claimValue}
                                        <Typography component="span" sx={{fontSize: "0.68rem", color: "text.secondary"}}> · {categoryById.get(c.categoryId)?.displayName ?? "—"}</Typography>
                                    </Typography>
                                    <Typography sx={{fontFamily: "monospace", fontSize: "0.72rem", color: "text.disabled"}}>{c.claimValue}</Typography>
                                    <Typography sx={{fontSize: "1.05rem", color: accent, fontWeight: 700, width: 18, textAlign: "center"}}>+</Typography>
                                </Box>
                            );
                        }) : <Typography sx={{p: 1.5, fontSize: "0.85rem", color: "text.secondary", fontStyle: "italic"}}>No matching permissions</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button variant="contained" onClick={savePermissions} disabled={setGroupClaims.isPending}>Save</Button>
                </DialogActions>
            </>
        );
    };

    const renderModal = () => {
        if (!modalType) return null;
        const title = modalType === "permissions"
            ? `Edit Permissions for Group ${detail?.name ?? selectedListItem?.name ?? ""}`
            : (draft.kind === "create" ? "New Group" : "Edit Details");
        return (
            <Dialog open onClose={closeModal} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                {modalType === "details" ? renderDetailsModal() : renderPermissionsModal()}
            </Dialog>
        );
    };

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Manage Groups" />
            <Box sx={{flex: 1, minHeight: 0, p: 3, display: "flex"}}>
                <Box sx={{flex: 1, display: "grid", gridTemplateColumns: "300px 1fr", border: `1px solid ${theme.palette.divider}`, borderRadius: "4px", overflow: "hidden", minHeight: 0}}>
                    {renderList()}
                    <Box sx={{display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto", backgroundColor: "white"}}>{renderDetail()}</Box>
                </Box>
            </Box>

            {renderModal()}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{vertical: "bottom", horizontal: "center"}}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default GroupsPage;
